import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaBalanceScale, FaArrowLeft, FaEdit, FaPhone, FaFax, FaEnvelope,
  FaMapMarkerAlt, FaSave, FaTimes, FaUser, FaGlobe, FaCheck, FaTimes as FaX,
  FaPlus, FaMoneyBillWave, FaCalendar, FaFileContract
} from 'react-icons/fa';
import './PretrialServiceDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PretrialServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchServiceDetails();
  }, [id]);

  const fetchServiceDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/pretrial-services/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setService(data.data);
        setStaff(data.data.staff || []);
        setError('');
      } else {
        setError('Pretrial service not found');
      }
    } catch (err) {
      console.error('Error fetching service details:', err);
      setError('Failed to load service details');
    } finally {
      setLoading(false);
    }
  };

  const getRecordTypeDisplay = (recordType) => {
    if (!recordType) return 'Unknown';
    const map = {
      'Government_County': 'County Government Program',
      'Private_Provider': 'Private Provider',
      'Private_Contract_County': 'County Contract Service',
      'Municipal_Court': 'Municipal Court Program',
      'Federal_Services': 'Federal Services',
      'No_Services': 'No Services Available',
      'State_Coordination': 'State Coordination'
    };
    return map[recordType] || recordType.replace(/_/g, ' ');
  };

  const ServiceCheckIcon = ({ value }) => {
    if (value === true) return <FaCheck className="service-icon-yes" />;
    if (value === false) return <FaX className="service-icon-no" />;
    return <span className="service-icon-unknown">?</span>;
  };

  if (loading) {
    return (
      <div className="service-detail-container">
        <div className="loading">Loading service details...</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-detail-container">
        <div className="error-message">{error || 'Service not found'}</div>
        <button className="btn-back" onClick={() => navigate('/address-book/pretrial-services')}>
          <FaArrowLeft /> Back to Pretrial Services
        </button>
      </div>
    );
  }

  return (
    <div className="service-detail-container">
      {/* Header */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/address-book/pretrial-services')}>
          <FaArrowLeft /> Back to Pretrial Services
        </button>

        <div className="header-title">
          <FaBalanceScale size={28} />
          <div>
            <h1>{service.organization_name || service.name}</h1>
            <div className="header-badges">
              <span className="record-type-badge">{getRecordTypeDisplay(service.record_type)}</span>
              {(service.county_jurisdiction || service.county) && (
                <span className="county-badge-large">
                  {service.county_jurisdiction || service.county} County
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="content-layout">
        {/* Contact Information Section */}
        <div className="service-info-section">
          <h2>Contact Information</h2>

          <div className="info-card-compact">
            <div className="compact-grid">
              <div className="compact-item">
                <label>Address</label>
                <span>
                  {service.address_street || 'N/A'}
                  <br />
                  {service.address_city || service.city}, {service.address_state || 'MT'} {service.address_zip}
                </span>
              </div>

              <div className="compact-item">
                <label><FaPhone /> Phone</label>
                <span>{service.phone || 'N/A'}</span>
              </div>

              {service.phone_alt && (
                <div className="compact-item">
                  <label><FaPhone /> Alt. Phone</label>
                  <span>{service.phone_alt}</span>
                </div>
              )}

              <div className="compact-item">
                <label><FaFax /> Fax</label>
                <span>{service.fax || 'N/A'}</span>
              </div>

              <div className="compact-item">
                <label><FaEnvelope /> Email</label>
                <span>
                  {service.email ? (
                    <a href={`mailto:${service.email}`}>{service.email}</a>
                  ) : 'N/A'}
                </span>
              </div>

              <div className="compact-item full-width">
                <label><FaGlobe /> Website</label>
                <span>
                  {service.website ? (
                    <a href={service.website} target="_blank" rel="noopener noreferrer">
                      {service.website}
                    </a>
                  ) : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Program Information */}
          <h2 style={{ marginTop: '2rem' }}>Program Information</h2>
          <div className="info-card-compact">
            <div className="compact-grid">
              <div className="compact-item">
                <label>Classification</label>
                <span className="classification-display">
                  {service.type_classification?.replace(/_/g, ' ') || 'N/A'}
                </span>
              </div>

              {service.program_start_year && (
                <div className="compact-item">
                  <label><FaCalendar /> Year Started</label>
                  <span>{service.program_start_year}</span>
                </div>
              )}

              {service.private_provider_contract && (
                <div className="compact-item">
                  <label><FaFileContract /> Contract Provider</label>
                  <span>{service.contract_provider_name || 'Yes'}</span>
                </div>
              )}

              {service.fees_info && (
                <div className="compact-item full-width">
                  <label><FaMoneyBillWave /> Fee Information</label>
                  <span className="fees-display">{service.fees_info}</span>
                </div>
              )}

              {service.services_supervision_levels && (
                <div className="compact-item full-width">
                  <label>Supervision Levels</label>
                  <span>{service.services_supervision_levels}</span>
                </div>
              )}
            </div>

            {service.notes && (
              <>
                <div className="info-divider"></div>
                <div className="compact-item full-width">
                  <label>Notes</label>
                  <p className="notes-text">{service.notes}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Services Offered & Staff Section */}
        <div className="service-staff-section">
          <h2>Services Offered</h2>
          <div className="services-offered-grid">
            <div className="service-item">
              <ServiceCheckIcon value={service.services_gps} />
              <span>GPS Monitoring</span>
            </div>
            <div className="service-item">
              <ServiceCheckIcon value={service.services_electronic_monitoring} />
              <span>Electronic Monitoring</span>
            </div>
            <div className="service-item">
              <ServiceCheckIcon value={service.services_scram_alcohol} />
              <span>SCRAM/Alcohol Monitoring</span>
            </div>
            <div className="service-item">
              <ServiceCheckIcon value={service.services_drug_testing} />
              <span>Drug Testing</span>
            </div>
            <div className="service-item">
              <ServiceCheckIcon value={service.services_check_ins} />
              <span>Check-Ins</span>
            </div>
            <div className="service-item">
              <ServiceCheckIcon value={service.services_court_reminders} />
              <span>Court Reminders</span>
            </div>
            <div className="service-item">
              <ServiceCheckIcon value={service.services_case_management} />
              <span>Case Management</span>
            </div>
            <div className="service-item">
              <ServiceCheckIcon value={service.services_risk_assessment} />
              <span>Risk Assessment</span>
            </div>
          </div>

          {/* Staff Contacts */}
          <h2 style={{ marginTop: '2rem' }}>Staff Contacts</h2>
          <div className="staff-list">
            {staff.length === 0 ? (
              <div className="no-staff-info">
                <FaUser size={48} />
                <p>No staff contacts available</p>
              </div>
            ) : (
              staff.map((member) => (
                <div key={member.id} className="staff-card">
                  <div className="staff-card-header">
                    <div className="staff-name-section">
                      <h3>{member.name}</h3>
                      {member.is_primary && <span className="primary-badge">Primary Contact</span>}
                    </div>
                    {member.title && <span className="title-badge-simple">{member.title}</span>}
                  </div>

                  <div className="staff-contact-info">
                    {member.phone && (
                      <div className="staff-contact-item">
                        <FaPhone className="staff-icon" />
                        <span>{member.phone}</span>
                      </div>
                    )}

                    {member.email && (
                      <div className="staff-contact-item">
                        <FaEnvelope className="staff-icon" />
                        <a href={`mailto:${member.email}`}>{member.email}</a>
                      </div>
                    )}

                    {member.notes && (
                      <div className="staff-notes">
                        {member.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PretrialServiceDetail;
